import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { 
  Calendar
} from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { 
  Clock, 
  Trash2, 
  Calculator, 
  Variable, 
  Shapes, 
  Sigma,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  ExternalLink,
  Inbox
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topicIcons = {
  arithmetic: { icon: Calculator, color: "icon-arithmetic" },
  algebra: { icon: Variable, color: "icon-algebra" },
  geometry: { icon: Shapes, color: "icon-geometry" },
  calculus: { icon: Sigma, color: "icon-calculus" },
  general: { icon: MessageCircle, color: "bg-primary/10 text-primary" }
};

export default function History() {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [dateFilter, setDateFilter] = useState(null);
  const [sessionId] = useState(() => {
    // Try to get existing session ID from localStorage
    let sid = localStorage.getItem('mathgenius_session_id');
    if (!sid) {
      sid = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('mathgenius_session_id', sid);
    }
    return sid;
  });

  useEffect(() => {
    loadHistory();
  }, [sessionId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/solutions/history/${sessionId}`);
      setSolutions(response.data.solutions || []);
    } catch (error) {
      console.error("Error loading history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/solutions/history/${sessionId}`);
      setSolutions([]);
      toast.success("History cleared");
    } catch (error) {
      toast.error("Failed to clear history");
    }
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Filter solutions by date if filter is set
  const filteredSolutions = dateFilter
    ? solutions.filter(sol => {
        const solDate = new Date(sol.timestamp);
        return format(solDate, 'yyyy-MM-dd') === format(dateFilter, 'yyyy-MM-dd');
      })
    : solutions;

  // Group solutions by date
  const groupedSolutions = filteredSolutions.reduce((groups, solution) => {
    const date = format(new Date(solution.timestamp), 'MMMM d, yyyy');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(solution);
    return groups;
  }, {});

  return (
    <div className="page-transition py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']" data-testid="history-title">
                Solution History
              </h1>
              <p className="text-muted-foreground">
                Review your past solutions and explanations
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Date Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  data-testid="date-filter-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateFilter ? format(dateFilter, 'MMM d') : 'Filter by date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end" data-testid="date-filter-popover">
                <Calendar
                  mode="single"
                  selected={dateFilter}
                  onSelect={setDateFilter}
                  data-testid="history-calendar"
                />
                {dateFilter && (
                  <div className="p-2 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setDateFilter(null)}
                      data-testid="clear-date-filter"
                    >
                      Clear filter
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Clear History */}
            {solutions.length > 0 && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={clearHistory}
                className="rounded-full text-destructive hover:text-destructive"
                data-testid="clear-history-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          // Loading State
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-slate-200 dark:border-slate-800">
                <CardContent className="p-6">
                  <div className="skeleton h-6 w-3/4 rounded mb-2"></div>
                  <div className="skeleton h-4 w-1/4 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSolutions.length === 0 ? (
          // Empty State
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold font-['Outfit'] mb-2">
                {dateFilter ? 'No solutions for this date' : 'No solutions yet'}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {dateFilter 
                  ? 'Try selecting a different date or clear the filter.'
                  : 'Start asking math questions to build your solution history.'
                }
              </p>
              {dateFilter ? (
                <Button 
                  variant="outline"
                  onClick={() => setDateFilter(null)}
                  className="rounded-full"
                  data-testid="clear-filter-btn"
                >
                  Clear Date Filter
                </Button>
              ) : (
                <Link to="/chat" data-testid="go-to-chat-btn">
                  <Button className="rounded-full">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Start Chatting
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          // Solutions List
          <div className="space-y-8" data-testid="solutions-list">
            {Object.entries(groupedSolutions).map(([date, dateSolutions]) => (
              <div key={date}>
                <h2 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  {date}
                </h2>
                <div className="space-y-4">
                  {dateSolutions.map((solution) => {
                    const topicInfo = topicIcons[solution.topic] || topicIcons.general;
                    const Icon = topicInfo.icon;
                    const isExpanded = expandedItems[solution.id];

                    return (
                      <Card 
                        key={solution.id}
                        className="history-item border-slate-200 dark:border-slate-800"
                        data-testid={`solution-card-${solution.id}`}
                      >
                        <CardContent className="p-0">
                          {/* Header - Clickable */}
                          <button
                            onClick={() => toggleItem(solution.id)}
                            className="w-full p-4 flex items-start gap-4 text-left hover:bg-accent/50 transition-colors rounded-t-lg"
                            data-testid={`solution-toggle-${solution.id}`}
                          >
                            <div className={`w-10 h-10 rounded-lg ${topicInfo.color} flex items-center justify-center shrink-0`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium line-clamp-2 mb-1">
                                {solution.problem}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(solution.timestamp), 'h:mm a')} • {solution.topic}
                              </p>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                            )}
                          </button>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="px-4 pb-4 pt-0">
                              <Separator className="mb-4" />
                              <div className="bg-muted rounded-xl p-4">
                                <h4 className="text-sm font-semibold mb-2">Solution</h4>
                                <ScrollArea className="max-h-64">
                                  <p className="whitespace-pre-wrap text-sm leading-relaxed math-content">
                                    {solution.solution}
                                  </p>
                                </ScrollArea>
                              </div>
                              <div className="mt-4 flex justify-end">
                                <Link to={`/chat/${solution.topic}`}>
                                  <Button variant="outline" size="sm" className="rounded-full">
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Continue in Chat
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {solutions.length > 0 && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="history-stats">
            <Card className="border-slate-200 dark:border-slate-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-primary mb-1">
                  {solutions.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Solutions</p>
              </CardContent>
            </Card>
            {Object.entries(
              solutions.reduce((acc, sol) => {
                acc[sol.topic] = (acc[sol.topic] || 0) + 1;
                return acc;
              }, {})
            ).slice(0, 3).map(([topic, count]) => {
              const topicInfo = topicIcons[topic] || topicIcons.general;
              const Icon = topicInfo.icon;
              return (
                <Card key={topic} className="border-slate-200 dark:border-slate-800">
                  <CardContent className="p-4 text-center">
                    <div className="flex justify-center mb-2">
                      <div className={`w-8 h-8 rounded-lg ${topicInfo.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold mb-1">{count}</p>
                    <p className="text-xs text-muted-foreground capitalize">{topic}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
